/**
 * Skills API — the `(user, skills)` stream as specs/api/learning-design.yaml
 * declares it and decomposes it by kind.
 *
 * This replaces the retired `/me/expertise` surface. The vocabulary changed
 * with the routes and the difference is not cosmetic:
 *
 *   - An attestation is now a SKILL that a fold derives. It has a `state`
 *     (proposed, held, rejected), a `visibility` (private, published) and a
 *     `basis` naming the kinds of record behind it. Nothing writes a skill; the
 *     records are written and the fold reports them.
 *   - The old promote flow is gone by decision. A colleague's endorsement and
 *     the owner's own declaration are two facts with two writers. The owner
 *     controls whether the skill is published, never what the colleague said.
 *   - The old 1-to-5 star peer rating is gone. An endorsement names a skill and
 *     carries an optional comment, and nothing else.
 *
 * Every response block below is REQUIRED in its schema. An `.optional()` block
 * that the backend later renames parses clean, yields `undefined`, and leaves a
 * dead surface with no error anywhere, which has happened in this repo before.
 * A field the contract declares nullable is `.nullable()`, which is a different
 * claim: `presence: null`, `period: null`, `target: null` and `skill: null`
 * each mean something specific and the UI renders that meaning rather than a
 * blank.
 *
 * Time is said one way, the way `tricorder-go/temporal/doc.go` says it: a
 * record carries `occurred` (when it happened in the world), `created` (when
 * the row entered the stream) and `sequence` (its position in the stream); a
 * view carries `updated`; an interval is a `period`. No key ends in `_at`, and
 * a civil date a nurse types keeps `_date`.
 */

import { z } from 'zod';

import { api, type ReadPage } from '../client';
import { PeriodSchema } from '../temporal';
import { parseResponse } from '../validation';
import { randomUUID } from '@/lib/uuid';

// ---------------------------------------------------------------------------
// Shared vocabulary
// ---------------------------------------------------------------------------

/**
 * A pointer to a resource, in the shape tricorder-go/reference uses everywhere.
 * Every field is optional in the contract and all three absent means "no
 * reference", which is different from a reference whose target is gone.
 */
const ReferenceSchema = z.object({
  reference: z.string().optional(),
  display: z.string().optional(),
  identifier: z.object({ system: z.string(), value: z.string() }).optional(),
});

/** A terminology coding that grounds a skill in a published vocabulary. */
const SkillCodingSchema = z.object({
  system: z.string(),
  code: z.string(),
  // `display` is the only field the contract leaves out of `required`, and the
  // gateway omits it when empty (`json:"display,omitempty"`).
  display: z.string().optional(),
});

/** Who may read a record. There is no third state. */
export const VISIBILITIES = ['private', 'published'] as const;
const VisibilitySchema = z.enum(VISIBILITIES);

/**
 * Where a skill stands on the practitioner's fold.
 *
 * `pending` is the one that is easy to mis-file. It is a skill on her list that
 * nothing supports YET and no curator raised: she has set a target on it, or a
 * qualification has been issued whose `period` has not started. It is never
 * `held`, so it counts toward no ward minimum, and it is never `proposed`, so
 * `:accept` and `:reject` must not be offered on it — `proposal` is null in
 * this state and both verbs answer 409 with nothing to act on.
 */
export const HELD_SKILL_STATES = ['proposed', 'pending', 'held', 'rejected'] as const;
const HeldSkillStateSchema = z.enum(HELD_SKILL_STATES);

/** The kind of record on the `(user, skills)` stream. */
export const RECORD_KINDS = [
  'declaration',
  'endorsement',
  'qualification',
  'attempt',
  'proposal',
  'target',
] as const;
const RecordKindSchema = z.enum(RECORD_KINDS);

/** Which record closes a target. */
export const TARGET_BASES = ['declared', 'qualified'] as const;
const TargetBasisSchema = z.enum(TARGET_BASES);

/**
 * Where a target stands. `dropped` when a drop record exists, else `achieved`
 * when the fold holds the skill on the target's `basis`, else `open`. A drop
 * wins over an achievement, because dropping is the one write the subject makes
 * after setting it.
 *
 * This is the ONLY answer to whether a target is done. The instant of the
 * transition is not lost: it is the `occurred` of the record that made it, on
 * the `/events` timeline.
 */
export const TARGET_STATES = ['open', 'achieved', 'dropped'] as const;
const TargetStateSchema = z.enum(TARGET_STATES);

// ---------------------------------------------------------------------------
// Knowledge: the skill catalogue
// ---------------------------------------------------------------------------

/**
 * One skill from the tenant catalogue. The catalogue is knowledge: a file in
 * the customer's repository, versioned with `period`, validated in CI and read
 * at runtime. Nothing on this API writes it.
 */
const SkillSchema = z.object({
  id: z.string(),
  // The skill's name per locale, keyed by BCP-47 tag. Every locale the
  // deployment supports is present, so a caller never falls back to an id.
  title: z.record(z.string(), z.string()),
  coding: SkillCodingSchema.nullable(),
  parent: ReferenceSchema.nullable(),
  version: z.string(),
  // When that catalogue version is in force. A successor's `start` ends its
  // predecessor, so `end` is null on the version in force and carries the
  // successor's start on every earlier one.
  period: PeriodSchema,
});

export type Skill = z.infer<typeof SkillSchema>;

// ---------------------------------------------------------------------------
// Records on the stream
// ---------------------------------------------------------------------------

const TargetSchema = z.object({
  id: z.string(),
  kind: z.literal('target'),
  visibility: VisibilitySchema,
  occurred: z.string(),
  created: z.string(),
  sequence: z.number(),
  performer: ReferenceSchema,
  delegator: ReferenceSchema.nullable(),
  subject: ReferenceSchema,
  skill_key: z.string(),
  skill: ReferenceSchema.nullable(),
  basis: TargetBasisSchema,
  // A civil date, not an instant: a trainee names a day, and a client compares
  // a day to today without a clock.
  due_date: z.string(),
  exam_date: z.string().nullable(),
  module_ids: z.array(z.string()),
  enrolment: ReferenceSchema.nullable(),
  // Where the target stands, derived by the fold and written by no route. It is
  // the ONE answer to "is this done": a client that compared `due_date` to a
  // clock would call a target achieved because a day passed.
  state: TargetStateSchema,
  // The `occurred` of the record that produced the current `state` — the
  // target's own record while it is open, the record that achieved the skill,
  // or the drop. A later record that folds into the skill without changing the
  // target leaves this where it was.
  updated: z.string(),
  // The TARGET's own version, which `:drop` takes as If-Match. It is not the
  // skill's: a colleague endorsing the same skill moves the skill's version and
  // must not 412 a drop.
  version: z.string(),
});

const DeclarationSchema = z.object({
  id: z.string(),
  kind: z.literal('declaration'),
  visibility: VisibilitySchema,
  occurred: z.string(),
  created: z.string(),
  sequence: z.number(),
  performer: ReferenceSchema,
  delegator: ReferenceSchema.nullable(),
  skill: ReferenceSchema.nullable(),
  label: z.string().nullable(),
  coding: SkillCodingSchema.nullable(),
  supersedes: ReferenceSchema.nullable(),
  evidence: z.array(ReferenceSchema),
});

const EndorsementSchema = z.object({
  id: z.string(),
  kind: z.literal('endorsement'),
  visibility: VisibilitySchema,
  occurred: z.string(),
  created: z.string(),
  sequence: z.number(),
  // The colleague who gave it. This is the one home for that fact; there is no
  // separate `from_user`, and there is no rating.
  performer: ReferenceSchema,
  delegator: ReferenceSchema.nullable(),
  subject: ReferenceSchema,
  skill_key: z.string(),
  skill: ReferenceSchema.nullable(),
  comment: z.string().nullable(),
});

const QualificationSchema = z.object({
  id: z.string(),
  kind: z.literal('qualification'),
  visibility: VisibilitySchema,
  // Distinct from `period.start`: a certificate can be recorded after the day
  // it takes effect, and the fold supports the skill from the later of the two.
  occurred: z.string(),
  created: z.string(),
  sequence: z.number(),
  performer: ReferenceSchema,
  delegator: ReferenceSchema.nullable(),
  subject: ReferenceSchema,
  // A qualification always names a catalogue skill: the institution verifies
  // against something the catalogue defines, never against a free label.
  skill: ReferenceSchema,
  issuer: ReferenceSchema,
  // When the qualification is in force, half-open. A certificate valid through
  // 31 December 2026 ends at 2027-01-01T00:00:00Z, so the last day it covers is
  // the day before `end`.
  period: PeriodSchema,
  evidence: ReferenceSchema.nullable(),
});

const AttemptSchema = z.object({
  id: z.string(),
  kind: z.literal('attempt'),
  visibility: VisibilitySchema,
  // On an attempt the world moment IS the completion, so it is the one instant
  // the record carries; a run reported later still folds at the moment it was
  // completed, and `created` is when the row entered the stream.
  occurred: z.string(),
  created: z.string(),
  sequence: z.number(),
  performer: ReferenceSchema,
  delegator: ReferenceSchema.nullable(),
  module: ReferenceSchema,
  module_version: z.string(),
  skills: z.array(ReferenceSchema),
  passed: z.boolean(),
  correct_count: z.number(),
  total_count: z.number(),
  session: ReferenceSchema.nullable(),
});

const ProposalSchema = z.object({
  id: z.string(),
  kind: z.literal('proposal'),
  // Always `private`: a proposal is a candidate, so there is nothing to consent
  // to until the owner accepts it.
  visibility: VisibilitySchema,
  occurred: z.string(),
  created: z.string(),
  sequence: z.number(),
  performer: ReferenceSchema,
  // An agent always acts under delegation, so a proposal always names who it
  // acted for.
  delegator: ReferenceSchema,
  skill: ReferenceSchema.nullable(),
  label: z.string().nullable(),
  coding: SkillCodingSchema.nullable(),
  // Why the curator raised it, in words the practitioner can judge. Accepting a
  // proposal without this on screen is accepting the curator's words blind.
  reasoning: z.string(),
  dedupe_key: z.string(),
  evidence: z.array(ReferenceSchema),
  run: ReferenceSchema.nullable(),
});

/**
 * One record on the stream, discriminated by `kind`.
 *
 * A discriminated union rather than a loose object: the timeline mixes five
 * shapes, and a reader that branched on which fields happen to be present would
 * read a declaration's null `skill` as a proposal with no catalogue skill.
 */
const SkillEventSchema = z.discriminatedUnion('kind', [
  DeclarationSchema,
  EndorsementSchema,
  QualificationSchema,
  AttemptSchema,
  ProposalSchema,
]);

// ---------------------------------------------------------------------------
// The fold on the party
// ---------------------------------------------------------------------------

const HeldSkillSchema = z.object({
  // How this item is addressed in a path: the catalogue id when the skill is in
  // the catalogue, the label's slug when it is not.
  key: z.string(),
  skill: ReferenceSchema.nullable(),
  label: z.string().nullable(),
  coding: SkillCodingSchema.nullable(),
  state: HeldSkillStateSchema,
  visibility: VisibilitySchema,
  // One entry per KIND of record behind the skill. A skill held on a
  // declaration alone and one held on a qualification are different claims, and
  // this array is how a reader tells them apart. Empty only in `proposed`.
  basis: z.array(RecordKindSchema),
  // When this item last changed: the `occurred` of the most recent event BY
  // SEQUENCE that this reader may see and that moved it — a record, a
  // visibility change or a proposal's rejection. A backdated correction
  // arriving at a higher sequence moves it backwards, which is the contract and
  // not a glitch.
  //
  // A `proposed` skill carries the proposal's own moment: the candidate is what
  // put the item on her list, so it is what dated it. Nullable for the one case
  // left — nothing this reader may see carries a moment at all.
  updated: z.string().nullable(),
  // When the fold holds this skill. `start` is the earliest support in the
  // basis; `end` is the qualification horizon, null when no qualification
  // supports the skill or one in force never ends. The whole period is null in
  // `proposed` and `rejected`, where no record supports it — a claim about the
  // skill, not a missing value.
  period: PeriodSchema.nullable(),
  // The fold's version, echoed in the ETag. Sent back as If-Match on the four
  // verbs, which is what makes a decision on a stale view fail loudly.
  version: z.string(),
  proposal: ReferenceSchema.nullable(),
  target: TargetSchema.nullable(),
});

/**
 * Where a practitioner is and whether they are free, read at the time of the
 * response and never cached.
 */
const PresenceSchema = z.object({
  name: z.string(),
  role: z.string(),
  on_shift_now: z.boolean(),
  // Present only when `on_shift_now`, and null otherwise, because the caseload
  // of a practitioner who is not on shift is not zero, it is not a question.
  patient_count: z.number().nullable(),
  team: ReferenceSchema.nullable(),
  same_team: z.boolean(),
});

/** The one list body every collection in this API answers with. Whether it
 *  continues is the response's `Link: rel="next"`, read by `api.getPage`. */
function pageOf<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
  });
}

const HeldSkillPageSchema = pageOf(HeldSkillSchema);

/**
 * A colleague's fold, with their presence alongside.
 *
 * `presence` is null when the subject has published no record. Publishing is
 * consent to be discoverable, so a practitioner who has published nothing has
 * consented to nothing: `items` is empty and their whereabouts are not
 * disclosed. Null says the question was not answered; it never means the
 * practitioner is off shift, and the UI must not render it as one.
 */
const HeldSkillsSchema = HeldSkillPageSchema.extend({
  subject: ReferenceSchema,
  presence: PresenceSchema.nullable(),
});

const SkillPageSchema = pageOf(SkillSchema);
const EndorsementPageSchema = pageOf(EndorsementSchema);
const QualificationPageSchema = pageOf(QualificationSchema);
const SkillEventPageSchema = pageOf(SkillEventSchema);
const TargetPageSchema = pageOf(TargetSchema);

export type Reference = z.infer<typeof ReferenceSchema>;
export type SkillCoding = z.infer<typeof SkillCodingSchema>;
export type Visibility = z.infer<typeof VisibilitySchema>;
export type HeldSkillState = z.infer<typeof HeldSkillStateSchema>;
export type RecordKind = z.infer<typeof RecordKindSchema>;
export type TargetBasis = z.infer<typeof TargetBasisSchema>;
export type TargetState = z.infer<typeof TargetStateSchema>;
export type HeldSkill = z.infer<typeof HeldSkillSchema>;
export type HeldSkillPage = ReadPage<z.infer<typeof HeldSkillPageSchema>>;
export type HeldSkills = ReadPage<z.infer<typeof HeldSkillsSchema>>;
export type Presence = z.infer<typeof PresenceSchema>;
export type Declaration = z.infer<typeof DeclarationSchema>;
export type Endorsement = z.infer<typeof EndorsementSchema>;
export type Qualification = z.infer<typeof QualificationSchema>;
export type Attempt = z.infer<typeof AttemptSchema>;
export type Proposal = z.infer<typeof ProposalSchema>;
export type SkillEvent = z.infer<typeof SkillEventSchema>;
export type SkillEventPage = ReadPage<z.infer<typeof SkillEventPageSchema>>;
export type Target = z.infer<typeof TargetSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/**
 * Declare a skill about yourself: a catalogue `skill_id`, a free `label`, or
 * both. Naming neither is refused with 422, because a declaration that names
 * nothing states nothing.
 */
export interface DeclareSkillRequest {
  skill_id?: string;
  label?: string;
  coding?: SkillCoding;
  visibility?: Visibility;
  supersedes?: string;
  evidence?: Reference[];
}

/** Endorse a colleague. The skill is the path's, so the body adds only words. */
export interface EndorseSkillRequest {
  comment?: string;
}

export interface RecordQualificationRequest {
  issuer: Reference;
  /**
   * When the qualification is in force, half-open. Send the instant AFTER the
   * last day it covers as `end`: a certificate valid through 31 December 2026
   * ends at `2027-01-01T00:00:00Z`. Omit `end` or send null when it does not
   * expire.
   */
  period: { start: string; end?: string | null };
  evidence?: Reference;
}

export interface SetTargetRequest {
  basis?: TargetBasis;
  due_date: string;
  exam_date?: string;
  module_ids?: string[];
}

// ---------------------------------------------------------------------------
// Paths and headers
// ---------------------------------------------------------------------------

const OWN_SKILLS = '/me/skills';

const key = (value: string) => encodeURIComponent(value);

/**
 * Headers for a write.
 *
 * Every mutation carries an `Idempotency-Key`: each appends a record to an
 * append-only stream, so a re-submit without one records the fact twice.
 *
 * The four verbs additionally carry `If-Match`, taken from the fold's own
 * `version`. Without it a decision taken on a view that has since moved is
 * applied silently to a state the nurse never saw.
 */
function writeHeaders(idempotencyKey: string, version?: string): { headers: Record<string, string> } {
  return {
    headers: {
      'Idempotency-Key': idempotencyKey,
      ...(version === undefined ? {} : { 'If-Match': version }),
    },
  };
}

function listQuery(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [name, value] of Object.entries(params)) {
    if (value !== undefined) query.set(name, String(value));
  }
  const qs = query.toString();
  return qs === '' ? '' : `?${qs}`;
}

// ---------------------------------------------------------------------------
// The catalogue
// ---------------------------------------------------------------------------

export interface ListSkillsParams extends PageParams {
  /** Return only the direct children of this skill id. Omit it for the whole catalogue. */
  parent?: string;
}

/** The skills in the catalogue version in force on the request date. */
export async function listSkills(
  params: ListSkillsParams = {},
): Promise<ReadPage<z.infer<typeof SkillPageSchema>>> {
  return api.getPage(`/skills${listQuery({ ...params })}`, { schema: SkillPageSchema });
}

export async function getSkill(skillId: string): Promise<Skill> {
  const body = await api.get<unknown>(`/skills/${key(skillId)}`);
  return parseResponse(SkillSchema, body, 'GET /skills/{id}');
}

// ---------------------------------------------------------------------------
// Your own skills
// ---------------------------------------------------------------------------

export interface ListOwnSkillsParams {
  state?: HeldSkillState;
  visibility?: Visibility;
  limit?: number;
  offset?: number;
}

/**
 * Your own fold, private and published alike, because this is your own stream
 * and nobody else reads it.
 */
export async function listOwnSkills(params: ListOwnSkillsParams = {}): Promise<HeldSkillPage> {
  return api.getPage(`${OWN_SKILLS}${listQuery({ ...params })}`, { schema: HeldSkillPageSchema });
}

export async function getOwnSkill(skillKey: string): Promise<HeldSkill> {
  const body = await api.get<unknown>(`${OWN_SKILLS}/${key(skillKey)}`);
  return parseResponse(HeldSkillSchema, body, 'GET /me/skills/{skill}');
}

/** Append a declaration to your own stream. 201 with the declaration. */
export async function declareOwnSkill(
  request: DeclareSkillRequest,
  idempotencyKey: string = randomUUID(),
): Promise<Declaration> {
  const body = await api.post<unknown>(OWN_SKILLS, request, writeHeaders(idempotencyKey));
  return parseResponse(DeclarationSchema, body, 'POST /me/skills');
}

/**
 * The four verbs that move a decision on your own fold. Each takes the fold's
 * `version` as `If-Match` and answers with the fold after the verb.
 *
 * They are one function because they differ in exactly one path segment; four
 * near-identical copies is how one of them would silently lose its `If-Match`.
 */
export const OWN_SKILL_VERBS = ['accept', 'reject', 'publish', 'unpublish'] as const;
export type OwnSkillVerb = (typeof OWN_SKILL_VERBS)[number];

export async function moveOwnSkill(
  skillKey: string,
  verb: OwnSkillVerb,
  version: string,
  idempotencyKey: string = randomUUID(),
): Promise<HeldSkill> {
  const path = `${OWN_SKILLS}/${key(skillKey)}:${verb}`;
  const body = await api.post<unknown>(path, undefined, writeHeaders(idempotencyKey, version));
  return parseResponse(HeldSkillSchema, body, `POST /me/skills/{skill}:${verb}`);
}

// ---------------------------------------------------------------------------
// A colleague's skills, and your own records read through the same routes
// ---------------------------------------------------------------------------

const userSkills = (userId: string) => `/users/${key(userId)}/skills`;

export interface ListUserSkillsParams {
  limit?: number;
  offset?: number;
}

/**
 * A colleague's fold with their presence. The subject reads their own records
 * through this route too, which is how the owner reads the endorsements they
 * have received: there is no `/me/skills/{skill}/endorsements`.
 */
export async function listUserSkills(
  userId: string,
  params: ListUserSkillsParams = {},
): Promise<HeldSkills> {
  return api.getPage(`${userSkills(userId)}${listQuery({ ...params })}`, {
    schema: HeldSkillsSchema,
  });
}

export async function getUserSkill(userId: string, skillKey: string): Promise<HeldSkill> {
  const path = `${userSkills(userId)}/${key(skillKey)}`;
  const body = await api.get<unknown>(path);
  return parseResponse(HeldSkillSchema, body, 'GET /users/{id}/skills/{skill}');
}

export interface PageParams {
  limit?: number;
  offset?: number;
}

export async function listUserSkillEndorsements(
  userId: string,
  skillKey: string,
  params: PageParams = {},
): Promise<ReadPage<z.infer<typeof EndorsementPageSchema>>> {
  const path = `${userSkills(userId)}/${key(skillKey)}/endorsements${listQuery({ ...params })}`;
  return api.getPage(path, { schema: EndorsementPageSchema });
}

export async function endorseUserSkill(
  userId: string,
  skillKey: string,
  request: EndorseSkillRequest,
  idempotencyKey: string = randomUUID(),
): Promise<Endorsement> {
  const path = `${userSkills(userId)}/${key(skillKey)}/endorsements`;
  const body = await api.post<unknown>(path, request, writeHeaders(idempotencyKey));
  return parseResponse(EndorsementSchema, body, 'POST /users/{id}/skills/{skill}/endorsements');
}

export interface ListQualificationsParams extends PageParams {
  /**
   * The temporal question, as `ap<instant>` for the qualifications in force at
   * that moment or `ap<start>/<end>` for the ones whose own period overlaps that
   * window. Omitted means every qualification, lapsed ones included, which is
   * what you want when you are looking at what expired — so this is the one
   * skills read whose absent period is NOT "now".
   */
  period?: string;
}

export async function listUserSkillQualifications(
  userId: string,
  skillKey: string,
  params: ListQualificationsParams = {},
): Promise<ReadPage<z.infer<typeof QualificationPageSchema>>> {
  const path = `${userSkills(userId)}/${key(skillKey)}/qualifications${listQuery({ ...params })}`;
  return api.getPage(path, { schema: QualificationPageSchema });
}

export async function recordUserSkillQualification(
  userId: string,
  skillKey: string,
  request: RecordQualificationRequest,
  idempotencyKey: string = randomUUID(),
): Promise<Qualification> {
  const path = `${userSkills(userId)}/${key(skillKey)}/qualifications`;
  const body = await api.post<unknown>(path, request, writeHeaders(idempotencyKey));
  return parseResponse(
    QualificationSchema,
    body,
    'POST /users/{id}/skills/{skill}/qualifications',
  );
}

/**
 * The records that fold into one skill, oldest first. This is how a fold is
 * audited, and it is the only route that carries a proposal's `reasoning`.
 */
export async function listUserSkillEvents(
  userId: string,
  skillKey: string,
  params: PageParams = {},
): Promise<SkillEventPage> {
  const path = `${userSkills(userId)}/${key(skillKey)}/events${listQuery({ ...params })}`;
  return api.getPage(path, { schema: SkillEventPageSchema });
}

// ---------------------------------------------------------------------------
// Targets
// ---------------------------------------------------------------------------

const ownTargets = (skillKey: string) => `${OWN_SKILLS}/${key(skillKey)}/targets`;
const userTargets = (userId: string, skillKey: string) =>
  `${userSkills(userId)}/${key(skillKey)}/targets`;

export async function listOwnSkillTargets(
  skillKey: string,
  params: PageParams = {},
): Promise<ReadPage<z.infer<typeof TargetPageSchema>>> {
  return api.getPage(`${ownTargets(skillKey)}${listQuery({ ...params })}`, { schema: TargetPageSchema });
}

export async function setOwnSkillTarget(
  skillKey: string,
  request: SetTargetRequest,
  idempotencyKey: string = randomUUID(),
): Promise<Target> {
  const body = await api.post<unknown>(ownTargets(skillKey), request, writeHeaders(idempotencyKey));
  return parseResponse(TargetSchema, body, 'POST /me/skills/{skill}/targets');
}

export async function getOwnSkillTarget(skillKey: string, targetId: string): Promise<Target> {
  const body = await api.get<unknown>(`${ownTargets(skillKey)}/${key(targetId)}`);
  return parseResponse(TargetSchema, body, 'GET /me/skills/{skill}/targets/{target_id}');
}

/**
 * Drop a target. `If-Match` is the TARGET's own version, not the skill's: a
 * colleague endorsing the same skill in between moves the skill's version and
 * must not 412 a drop that has nothing to do with it.
 */
export async function dropOwnSkillTarget(
  skillKey: string,
  targetId: string,
  version: string,
  idempotencyKey: string = randomUUID(),
): Promise<Target> {
  const path = `${ownTargets(skillKey)}/${key(targetId)}:drop`;
  const body = await api.post<unknown>(path, undefined, writeHeaders(idempotencyKey, version));
  return parseResponse(TargetSchema, body, 'POST /me/skills/{skill}/targets/{target_id}:drop');
}

export async function listUserSkillTargets(
  userId: string,
  skillKey: string,
  params: PageParams = {},
): Promise<ReadPage<z.infer<typeof TargetPageSchema>>> {
  const path = `${userTargets(userId, skillKey)}${listQuery({ ...params })}`;
  return api.getPage(path, { schema: TargetPageSchema });
}

export async function setUserSkillTarget(
  userId: string,
  skillKey: string,
  request: SetTargetRequest,
  idempotencyKey: string = randomUUID(),
): Promise<Target> {
  const path = userTargets(userId, skillKey);
  const body = await api.post<unknown>(path, request, writeHeaders(idempotencyKey));
  return parseResponse(TargetSchema, body, 'POST /users/{id}/skills/{skill}/targets');
}

export async function getUserSkillTarget(
  userId: string,
  skillKey: string,
  targetId: string,
): Promise<Target> {
  const path = `${userTargets(userId, skillKey)}/${key(targetId)}`;
  const body = await api.get<unknown>(path);
  return parseResponse(TargetSchema, body, 'GET /users/{id}/skills/{skill}/targets/{target_id}');
}

export async function dropUserSkillTarget(
  userId: string,
  skillKey: string,
  targetId: string,
  version: string,
  idempotencyKey: string = randomUUID(),
): Promise<Target> {
  const path = `${userTargets(userId, skillKey)}/${key(targetId)}:drop`;
  const body = await api.post<unknown>(path, undefined, writeHeaders(idempotencyKey, version));
  return parseResponse(
    TargetSchema,
    body,
    'POST /users/{id}/skills/{skill}/targets/{target_id}:drop',
  );
}

/**
 * Exported for the tests that assert a body against the exact wire shape. The
 * app itself reads through the functions above, which parse before returning.
 */
export const skillSchemas = {
  Skill: SkillSchema,
  SkillPage: SkillPageSchema,
  HeldSkill: HeldSkillSchema,
  HeldSkillPage: HeldSkillPageSchema,
  HeldSkills: HeldSkillsSchema,
  Declaration: DeclarationSchema,
  Endorsement: EndorsementSchema,
  EndorsementPage: EndorsementPageSchema,
  Qualification: QualificationSchema,
  QualificationPage: QualificationPageSchema,
  Attempt: AttemptSchema,
  Proposal: ProposalSchema,
  SkillEvent: SkillEventSchema,
  SkillEventPage: SkillEventPageSchema,
  Target: TargetSchema,
  TargetPage: TargetPageSchema,
  Presence: PresenceSchema,
} as const;
